'use client';

import React, { useState, useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface RegionTarget {
  id: string;
  type: 'geographical' | 'ideological' | 'conceptual';
  name: string;
  description: string;
  keywords: string[];
  color: string;
  active: boolean;
}

interface CrawlSource {
  id: string;
  name: string;
  url: string;
  type: 'rss' | 'full-page' | 'social' | 'blog-archive' | 'news-search';
  region: string;
  active: boolean;
  lastCrawled?: string;
  status?: 'success' | 'error' | 'pending';
}

interface CrawlLog {
  timestamp: string;
  level: 'info' | 'success' | 'error' | 'warn';
  message: string;
  source?: string;
}

// ─── CLAUDE SYSTEM PROMPTS ────────────────────────────────────────────────────

const CLAUDE_SYSTEM_PROMPTS = {
  visionMission: `You are the MANA Crawler Intelligence — an autonomous research agent for the Muslim Alliance in North America (MANA) newsletter. Your mission is to identify, evaluate, and surface content that serves African-American Muslim communities across the United States.

VISION: Surface authentic, community-grounded information that strengthens the bonds of African-American Muslim families, documents our history, amplifies our voices, and connects our communities to resources and each other.

MISSION PARAMETERS:
- Prioritize content from Black Muslim voices, organizations, and scholars
- Seek stories of resilience, community building, and spiritual development
- Document environmental justice issues affecting communities of color
- Track civic engagement, policy, and justice initiatives
- Surface marriage, family, and youth development resources
- Connect historical legacy to present-day community action

CONTENT CATEGORIES (in priority order):
1. marriage-family: Marriage preparation, family dynamics, parenting in deen
2. history-heritage: African-American Muslim history, legacy figures, cultural preservation
3. legacy-profiles: Community leaders, scholars, elders, changemakers
4. youth-young-adults: Youth programs, education, coming-of-age in Islam
5. faith-ramadan: Spiritual development, Quran, Sunnah, seasonal observances
6. justice-civic: Environmental justice, criminal justice reform, civic engagement

RELEVANCE THRESHOLD: Score articles 0-100. Only surface articles scoring 65+.
AUTHENTICITY RULE: Never invent, fabricate, or hallucinate article content. Only process real content you have been given.`,

  rssEvaluation: `You are evaluating RSS feed sources for the MANA newsletter crawler. For each source, assess:

1. COMMUNITY ALIGNMENT (0-40 pts): Does this source primarily serve or speak to African-American Muslim communities?
2. CONTENT QUALITY (0-30 pts): Is the content substantive, well-sourced, and community-grounded?
3. PUBLICATION FREQUENCY (0-15 pts): Does this source publish regularly (weekly or more)?
4. RELEVANCE BREADTH (0-15 pts): Does it cover multiple MANA content categories?

Return a JSON object with: { score, recommendation, categories, notes }
Only recommend sources scoring 60+.`,

  categoryTuning: `You are tuning content categorization for the MANA newsletter. Given an article title, URL, and description, assign:

1. PRIMARY CATEGORY: The single best-fit category from: marriage-family, history-heritage, legacy-profiles, youth-young-adults, faith-ramadan, justice-civic
2. SECONDARY CATEGORIES: Up to 2 additional relevant categories
3. RELEVANCE SCORE: 0-100 based on alignment with African-American Muslim community needs
4. RELEVANCE REASON: 1-2 sentences explaining why this article matters to the community
5. TAGS: 3-5 specific tags (e.g., "environmental-justice", "black-muslim-history", "youth-programs")

Return JSON: { primaryCategory, secondaryCategories, relevanceScore, relevanceReason, tags }`,

  failureRecovery: `You are the MANA Crawler failure recovery agent. When a crawl source fails, analyze the error and recommend recovery actions.

ERROR TYPES AND RECOVERY STRATEGIES:
- HTTP 404/410: Source URL has moved or been removed → Search for new URL, check robots.txt, try alternative paths
- HTTP 403/401: Access denied → Try without auth headers, check if RSS requires registration, find public mirror
- HTTP 429: Rate limited → Implement exponential backoff, reduce crawl frequency, try at off-peak hours
- DNS failure: Domain unreachable → Check if domain is still active, look for mirror sites
- Parse error: RSS/HTML malformed → Try alternative parsers, extract content directly from HTML
- Timeout: Source too slow → Increase timeout, try at different times, check if CDN is available
- Empty feed: No new content → Reduce crawl frequency, check if source has moved to different platform

Return JSON: { recoveryAction, alternativeUrls, recommendedFrequency, notes }`,

  regionalCrawl: `You are the MANA Regional Intelligence agent. You are analyzing content from a specific geographical or ideological region to identify material relevant to African-American Muslim communities.

REGIONAL CONTEXT: {REGION_NAME}
REGION TYPE: {REGION_TYPE}
REGION DESCRIPTION: {REGION_DESCRIPTION}

For each piece of content found in this region, evaluate:
1. Does it directly affect or involve African-American Muslim communities in this region?
2. Does it document environmental, social, or civic conditions in this area?
3. Does it feature voices, organizations, or institutions from this community?
4. Does it connect to broader national issues affecting Black Muslim communities?

SCORING: Only surface content scoring 65+ on community relevance.
CONTEXT: Always note the specific regional connection in your relevance reason.`,
};

// ─── DEFAULT REGION TARGETS ───────────────────────────────────────────────────

const DEFAULT_REGIONS: RegionTarget[] = [
  {
    id: 'pittsburgh-braddock',
    type: 'geographical',
    name: 'Pittsburgh / Braddock, PA',
    description: 'Steel corridor communities, environmental justice, Braddock PA and surrounding areas',
    keywords: ['Braddock', 'Pittsburgh', 'Edgar Thomson', 'environmental justice', 'steel mill', 'Mon Valley'],
    color: '#c9a84c',
    active: true,
  },
  {
    id: 'detroit-river-rouge',
    type: 'geographical',
    name: 'Detroit / River Rouge, MI',
    description: 'River Rouge 48217, most polluted zip code, industrial corridor communities',
    keywords: ['River Rouge', 'Detroit', '48217', 'Rouge River', 'environmental racism', 'Dearborn'],
    color: '#2d8a87',
    active: true,
  },
  {
    id: 'chicago-south-west',
    type: 'geographical',
    name: 'Chicago South/West Sides',
    description: 'South and West Side communities, Cook County, CIOGC, MAS Chicago',
    keywords: ['Chicago South Side', 'Chicago West Side', 'Cook County', 'CIOGC', 'environmental justice Chicago'],
    color: '#8a8ab8',
    active: true,
  },
  {
    id: 'black-muslim-national',
    type: 'ideological',
    name: 'African-American Muslim National',
    description: 'National African-American Muslim discourse, organizations, and community news',
    keywords: ['African American Muslim', 'Black Muslim', 'MANA', 'Imam W.D. Mohammed', 'Nation of Islam history', 'Black Islam'],
    color: '#2d7a47',
    active: true,
  },
  {
    id: 'environmental-justice',
    type: 'conceptual',
    name: 'Environmental Justice',
    description: 'Environmental racism, pollution in communities of color, climate justice',
    keywords: ['environmental racism', 'environmental justice', 'pollution communities of color', 'climate justice', 'BIPOC environment'],
    color: '#c47a7a',
    active: true,
  },
  {
    id: 'criminal-justice-reform',
    type: 'conceptual',
    name: 'Criminal Justice Reform',
    description: 'Mass incarceration, prison reform, reentry, Muslim chaplaincy',
    keywords: ['mass incarceration', 'prison reform', 'Muslim chaplain', 'reentry', 'criminal justice reform', 'formerly incarcerated'],
    color: '#D4A82A',
    active: false,
  },
];

// ─── DEFAULT CRAWL SOURCES ────────────────────────────────────────────────────

const DEFAULT_SOURCES: CrawlSource[] = [
  { id: 's1', name: 'MANA Justice', url: 'https://manajustice.org/feed/', type: 'rss', region: 'black-muslim-national', active: true },
  { id: 's2', name: 'Sapelo Square', url: 'https://sapelo.org/feed/', type: 'rss', region: 'black-muslim-national', active: true },
  { id: 's3', name: 'Yaqeen Institute', url: 'https://yaqeeninstitute.org/feed/', type: 'rss', region: 'black-muslim-national', active: true },
  { id: 's4', name: 'Muslim Matters', url: 'https://muslimmatters.org/feed/', type: 'rss', region: 'black-muslim-national', active: true },
  { id: 's5', name: 'Pittsburgh Post-Gazette Environment', url: 'https://www.post-gazette.com/rss/environment', type: 'rss', region: 'pittsburgh-braddock', active: true },
  { id: 's6', name: 'Detroit News Environment', url: 'https://www.detroitnews.com/rss/news/', type: 'rss', region: 'detroit-river-rouge', active: true },
  { id: 's7', name: 'Chicago Tribune South Side', url: 'https://www.chicagotribune.com/arcio/rss/', type: 'rss', region: 'chicago-south-west', active: true },
  { id: 's8', name: 'Black Muslim Psychology Blog', url: 'https://blackmuslimpsychology.com/feed/', type: 'rss', region: 'black-muslim-national', active: true },
];

// ─── COMPONENTS ───────────────────────────────────────────────────────────────

function RegionCard({ region, onToggle, onEdit }: {
  region: RegionTarget;
  onToggle: (id: string) => void;
  onEdit: (region: RegionTarget) => void;
}) {
  const typeColors: Record<string, string> = {
    geographical: '#c9a84c',
    ideological: '#8a8ab8',
    conceptual: '#2d8a87',
  };

  return (
    <div style={{
      background: region.active ? 'rgba(6,13,18,0.8)' : 'rgba(6,13,18,0.4)',
      border: `1px solid ${region.active ? `${region.color}40` : 'rgba(255,255,255,0.06)'}`,
      borderLeft: `3px solid ${region.active ? region.color : 'rgba(255,255,255,0.1)'}`,
      borderRadius: 8,
      padding: '16px 18px',
      transition: 'all 0.2s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{
              fontFamily: 'monospace',
              fontSize: 7,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: typeColors[region.type],
              background: `${typeColors[region.type]}15`,
              border: `1px solid ${typeColors[region.type]}30`,
              borderRadius: 3,
              padding: '2px 6px',
            }}>
              {region.type}
            </span>
          </div>
          <h4 style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: region.active ? '#F5F0E8' : 'rgba(212,197,169,0.4)', margin: 0, fontWeight: 400 }}>
            {region.name}
          </h4>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            onClick={() => onEdit(region)}
            style={{
              fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em',
              padding: '4px 8px', borderRadius: 4, cursor: 'pointer',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(212,197,169,0.5)',
            }}
          >
            Edit
          </button>
          <button
            onClick={() => onToggle(region.id)}
            style={{
              fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em',
              padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
              background: region.active ? `${region.color}15` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${region.active ? `${region.color}40` : 'rgba(255,255,255,0.1)'}`,
              color: region.active ? region.color : 'rgba(212,197,169,0.3)',
            }}
          >
            {region.active ? 'Active' : 'Off'}
          </button>
        </div>
      </div>
      <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.45)', margin: '0 0 10px', lineHeight: 1.5 }}>
        {region.description}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {region.keywords.slice(0, 4).map(kw => (
          <span key={kw} style={{
            fontFamily: 'monospace', fontSize: 8,
            color: region.active ? `${region.color}90` : 'rgba(212,197,169,0.2)',
            background: region.active ? `${region.color}08` : 'rgba(255,255,255,0.02)',
            border: `1px solid ${region.active ? `${region.color}20` : 'rgba(255,255,255,0.05)'}`,
            borderRadius: 3, padding: '2px 6px',
          }}>
            {kw}
          </span>
        ))}
        {region.keywords.length > 4 && (
          <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.25)' }}>
            +{region.keywords.length - 4} more
          </span>
        )}
      </div>
    </div>
  );
}

function CrawlLogLine({ log }: { log: CrawlLog }) {
  const colors: Record<string, string> = {
    info: 'rgba(212,197,169,0.5)',
    success: '#2d7a47',
    error: '#c47a7a',
    warn: '#c9a84c',
  };
  const prefixes: Record<string, string> = {
    info: '→',
    success: '✓',
    error: '✗',
    warn: '⚠',
  };
  return (
    <div style={{ display: 'flex', gap: 10, padding: '3px 0', fontFamily: 'monospace', fontSize: 10 }}>
      <span style={{ color: 'rgba(212,197,169,0.25)', flexShrink: 0 }}>{log.timestamp}</span>
      <span style={{ color: colors[log.level], flexShrink: 0 }}>{prefixes[log.level]}</span>
      {log.source && <span style={{ color: 'rgba(201,168,76,0.5)', flexShrink: 0 }}>[{log.source}]</span>}
      <span style={{ color: colors[log.level] }}>{log.message}</span>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function MANACrawlerPage() {
  const [activeTab, setActiveTab] = useState<'regions' | 'sources' | 'prompts' | 'crawl' | 'results'>('regions');
  const [regions, setRegions] = useState<RegionTarget[]>(DEFAULT_REGIONS);
  const [sources, setSources] = useState<CrawlSource[]>(DEFAULT_SOURCES);
  const [crawlLogs, setCrawlLogs] = useState<CrawlLog[]>([]);
  const [isCrawling, setIsCrawling] = useState(false);
  const [activePrompt, setActivePrompt] = useState<keyof typeof CLAUDE_SYSTEM_PROMPTS>('visionMission');
  const [editingRegion, setEditingRegion] = useState<RegionTarget | null>(null);
  const [newRegionForm, setNewRegionForm] = useState({ name: '', description: '', type: 'geographical' as RegionTarget['type'], keywords: '' });
  const [claudeQuery, setClaudeQuery] = useState('');
  const [crawlDepth, setCrawlDepth] = useState<'rss-only' | 'full-page' | 'deep'>('rss-only');
  const logsEndRef = useRef<HTMLDivElement>(null);

  const { response, isLoading, error, sendMessage } = useChat('ANTHROPIC', 'claude-sonnet-4-5-20250929', true);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [crawlLogs]);

  const addLog = (level: CrawlLog['level'], message: string, source?: string) => {
    setCrawlLogs(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      level,
      message,
      source,
    }]);
  };

  const toggleRegion = (id: string) => {
    setRegions(prev => prev.map(r => r.id === id ? { ...r, active: !r.active } : r));
  };

  const addRegion = () => {
    if (!newRegionForm.name.trim()) return;
    const newRegion: RegionTarget = {
      id: `region-${Date.now()}`,
      type: newRegionForm.type,
      name: newRegionForm.name,
      description: newRegionForm.description,
      keywords: newRegionForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
      color: ['#c9a84c', '#2d8a87', '#8a8ab8', '#2d7a47', '#c47a7a'][Math.floor(Math.random() * 5)],
      active: true,
    };
    setRegions(prev => [...prev, newRegion]);
    setNewRegionForm({ name: '', description: '', type: 'geographical', keywords: '' });
    toast.success('Region target added');
  };

  const runClaudeAnalysis = () => {
    if (!claudeQuery.trim()) return;
    const systemPrompt = CLAUDE_SYSTEM_PROMPTS[activePrompt]
      .replace('{REGION_NAME}', regions.find(r => r.active)?.name ?? 'All Active Regions')
      .replace('{REGION_TYPE}', regions.find(r => r.active)?.type ?? 'geographical')
      .replace('{REGION_DESCRIPTION}', regions.find(r => r.active)?.description ?? '');

    sendMessage([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: claudeQuery },
    ], { temperature: 0.3, max_tokens: 2000 });
  };

  const simulateCrawl = async () => {
    setIsCrawling(true);
    setCrawlLogs([]);
    setActiveTab('crawl');

    const activeSources = sources.filter(s => s.active);
    const activeRegions = regions.filter(r => r.active);

    addLog('info', `Starting MANA crawler — ${activeSources.length} sources, ${activeRegions.length} regions`);
    addLog('info', `Crawl depth: ${crawlDepth}`);
    addLog('info', `Claude model: claude-sonnet-4-5-20250929`);

    for (const region of activeRegions) {
      addLog('info', `Targeting region: ${region.name}`, region.type);
      await new Promise(r => setTimeout(r, 300));
    }

    for (const source of activeSources) {
      addLog('info', `Polling: ${source.url}`, source.name);
      await new Promise(r => setTimeout(r, 400));

      // Simulate success/failure
      const success = Math.random() > 0.2;
      if (success) {
        const count = Math.floor(Math.random() * 8) + 1;
        addLog('success', `Fetched ${count} articles from RSS feed`, source.name);
        if (crawlDepth !== 'rss-only') {
          addLog('info', `Scraping full page content for ${count} articles...`, source.name);
          await new Promise(r => setTimeout(r, 600));
          addLog('success', `Full-page content extracted`, source.name);
        }
        addLog('info', `Sending to Claude for categorization...`, source.name);
        await new Promise(r => setTimeout(r, 500));
        const inserted = Math.floor(count * 0.7);
        addLog('success', `${inserted}/${count} articles passed relevance threshold (65+)`, source.name);
      } else {
        addLog('error', `HTTP 403 — Access denied. Attempting recovery...`, source.name);
        await new Promise(r => setTimeout(r, 300));
        addLog('warn', `Recovery: Trying alternative RSS path`, source.name);
      }
    }

    addLog('success', `Crawl complete — check Results tab for extracted articles`);
    setIsCrawling(false);
    toast.success('Crawl complete');
  };

  const tabs = [
    { id: 'regions', label: 'Region Targets', icon: '⬡' },
    { id: 'sources', label: 'Sources', icon: '◈' },
    { id: 'prompts', label: 'Claude Prompts', icon: '✦' },
    { id: 'crawl', label: 'Crawl Log', icon: '◎' },
    { id: 'results', label: 'Results', icon: '▣' },
  ] as const;

  return (
    <div style={{ fontFamily: "'acumin-pro', 'Helvetica Neue', Helvetica, Arial, sans-serif", backgroundColor: '#060d12', minHeight: '100vh' }}>
      <Header />

      {/* Page header */}
      <div style={{
        background: 'linear-gradient(180deg, rgba(45,122,71,0.08) 0%, transparent 100%)',
        borderBottom: '1px solid rgba(45,122,71,0.15)',
        padding: '32px 24px 24px',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.6)', marginBottom: 8 }}>
            ⬡ MANA Intelligence System
          </p>
          <h1 style={{ fontFamily: "'Amiri', serif", fontSize: 32, color: '#F5F0E8', fontWeight: 400, margin: '0 0 8px' }}>
            Advanced Web Crawler
          </h1>
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,197,169,0.6)', margin: '0 0 20px', lineHeight: 1.7, maxWidth: 700 }}>
            Claude-powered autonomous crawler with geographical and ideological region targeting. Goes beyond RSS — scrapes full pages, social feeds, blog archives, and regional news sources relevant to African-American Muslim communities.
          </p>

          {/* Status bar */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 20 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#2d7a47' }} />
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase' }}>
                {regions.filter(r => r.active).length} Active Regions
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 20 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#c9a84c' }} />
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase' }}>
                {sources.filter(s => s.active).length} Active Sources
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 20 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d8a87', letterSpacing: '.1em', textTransform: 'uppercase' }}>
                Claude: claude-sonnet-4-5-20250929
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div style={{
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        padding: '0 24px',
        display: 'flex', gap: 0, overflowX: 'auto',
        maxWidth: 1200, margin: '0 auto',
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '14px 18px',
              background: 'none', border: 'none',
              borderBottom: `2px solid ${activeTab === tab.id ? '#c9a84c' : 'transparent'}`,
              cursor: 'pointer',
              fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
              color: activeTab === tab.id ? '#c9a84c' : 'rgba(212,197,169,0.4)',
              transition: 'all 0.15s',
              flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <span style={{ color: activeTab === tab.id ? '#c9a84c' : 'rgba(212,197,169,0.3)' }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Main content */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px 80px' }}>

        {/* ── REGION TARGETS TAB ── */}
        {activeTab === 'regions' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h2 style={{ fontFamily: "'Amiri', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 400, margin: '0 0 4px' }}>
                  Region Targets
                </h2>
                <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', margin: 0 }}>
                  Define geographical, ideological, and conceptual regions to focus the crawler
                </p>
              </div>
            </div>

            {/* Region grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12, marginBottom: 32 }}>
              {regions.map(region => (
                <RegionCard
                  key={region.id}
                  region={region}
                  onToggle={toggleRegion}
                  onEdit={setEditingRegion}
                />
              ))}
            </div>

            {/* Add new region */}
            <div style={{
              background: 'rgba(6,13,18,0.6)',
              border: '1px solid rgba(201,168,76,0.15)',
              borderRadius: 10,
              padding: '24px 28px',
            }}>
              <h3 style={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.6)', margin: '0 0 16px' }}>
                ◈ Add New Region Target
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                    Region Name
                  </label>
                  <input
                    value={newRegionForm.name}
                    onChange={e => setNewRegionForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Atlanta / West End"
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 6,
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                      color: '#F5F0E8', fontFamily: 'monospace', fontSize: 11,
                      outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                    Type
                  </label>
                  <select
                    value={newRegionForm.type}
                    onChange={e => setNewRegionForm(p => ({ ...p, type: e.target.value as RegionTarget['type'] }))}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 6,
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                      color: '#F5F0E8', fontFamily: 'monospace', fontSize: 11,
                      outline: 'none',
                    }}
                  >
                    <option value="geographical">Geographical</option>
                    <option value="ideological">Ideological</option>
                    <option value="conceptual">Conceptual</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                  Description
                </label>
                <input
                  value={newRegionForm.description}
                  onChange={e => setNewRegionForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description of this region and its relevance"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                    color: '#F5F0E8', fontFamily: 'monospace', fontSize: 11,
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                  Keywords (comma-separated)
                </label>
                <input
                  value={newRegionForm.keywords}
                  onChange={e => setNewRegionForm(p => ({ ...p, keywords: e.target.value }))}
                  placeholder="Atlanta, West End, environmental justice, Black Muslim Atlanta"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                    color: '#F5F0E8', fontFamily: 'monospace', fontSize: 11,
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
              <button
                onClick={addRegion}
                style={{
                  fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  padding: '10px 20px', borderRadius: 6, cursor: 'pointer',
                  background: 'rgba(45,122,71,0.12)', border: '1px solid rgba(45,122,71,0.3)',
                  color: '#2d7a47',
                }}
              >
                ⬡ Add Region Target
              </button>
            </div>
          </div>
        )}

        {/* ── SOURCES TAB ── */}
        {activeTab === 'sources' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontFamily: "'Amiri', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 400, margin: '0 0 4px' }}>
                Crawl Sources
              </h2>
              <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', margin: 0 }}>
                RSS feeds, full-page scrapers, social feeds, and blog archives
              </p>
            </div>

            {/* Crawl depth selector */}
            <div style={{
              background: 'rgba(6,13,18,0.6)',
              border: '1px solid rgba(201,168,76,0.15)',
              borderRadius: 8,
              padding: '16px 20px',
              marginBottom: 20,
              display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            }}>
              <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.6)', letterSpacing: '.1em', textTransform: 'uppercase' }}>
                Crawl Depth:
              </span>
              {(['rss-only', 'full-page', 'deep'] as const).map(depth => (
                <button
                  key={depth}
                  onClick={() => setCrawlDepth(depth)}
                  style={{
                    fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase',
                    padding: '6px 14px', borderRadius: 4, cursor: 'pointer',
                    background: crawlDepth === depth ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${crawlDepth === depth ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.08)'}`,
                    color: crawlDepth === depth ? '#c9a84c' : 'rgba(212,197,169,0.4)',
                  }}
                >
                  {depth === 'rss-only' ? '◈ RSS Only' : depth === 'full-page' ? '▣ Full Page' : '⬡ Deep Archive'}
                </button>
              ))}
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', marginLeft: 'auto' }}>
                {crawlDepth === 'rss-only' ? 'Parse RSS/Atom feeds only' :
                  crawlDepth === 'full-page'? 'RSS + scrape full article content' : 'RSS + full pages + blog archives + social feeds'}
              </span>
            </div>

            {/* Sources list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sources.map(source => {
                const region = regions.find(r => r.id === source.region);
                return (
                  <div
                    key={source.id}
                    style={{
                      background: 'rgba(6,13,18,0.6)',
                      border: `1px solid ${source.active ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.05)'}`,
                      borderRadius: 8,
                      padding: '14px 18px',
                      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: source.active ? '#F5F0E8' : 'rgba(212,197,169,0.4)', marginBottom: 3 }}>
                        {source.name}
                      </div>
                      <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', wordBreak: 'break-all' }}>
                        {source.url}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{
                        fontFamily: 'monospace', fontSize: 7, letterSpacing: '.08em', textTransform: 'uppercase',
                        color: '#2d8a87', background: 'rgba(45,138,135,0.1)', border: '1px solid rgba(45,138,135,0.2)',
                        borderRadius: 3, padding: '2px 6px',
                      }}>
                        {source.type}
                      </span>
                      {region && (
                        <span style={{
                          fontFamily: 'monospace', fontSize: 7, letterSpacing: '.06em',
                          color: `${region.color}90`, background: `${region.color}08`,
                          border: `1px solid ${region.color}20`, borderRadius: 3, padding: '2px 6px',
                        }}>
                          {region.name}
                        </span>
                      )}
                      <button
                        onClick={() => setSources(prev => prev.map(s => s.id === source.id ? { ...s, active: !s.active } : s))}
                        style={{
                          fontFamily: 'monospace', fontSize: 8, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                          background: source.active ? 'rgba(45,122,71,0.1)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${source.active ? 'rgba(45,122,71,0.3)' : 'rgba(255,255,255,0.1)'}`,
                          color: source.active ? '#2d7a47' : 'rgba(212,197,169,0.3)',
                        }}
                      >
                        {source.active ? 'Active' : 'Off'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── CLAUDE PROMPTS TAB ── */}
        {activeTab === 'prompts' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontFamily: "'Amiri', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 400, margin: '0 0 4px' }}>
                Claude System Prompts
              </h2>
              <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', margin: 0 }}>
                Autonomous vision/mission definition, source evaluation, category tuning, and failure recovery
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20 }}>
              {/* Prompt selector */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {(Object.keys(CLAUDE_SYSTEM_PROMPTS) as Array<keyof typeof CLAUDE_SYSTEM_PROMPTS>).map(key => {
                  const labels: Record<string, string> = {
                    visionMission: 'Vision / Mission',
                    rssEvaluation: 'RSS Evaluation',
                    categoryTuning: 'Category Tuning',
                    failureRecovery: 'Failure Recovery',
                    regionalCrawl: 'Regional Crawl',
                  };
                  const icons: Record<string, string> = {
                    visionMission: '✦',
                    rssEvaluation: '◈',
                    categoryTuning: '⬡',
                    failureRecovery: '◎',
                    regionalCrawl: '▣',
                  };
                  return (
                    <button
                      key={key}
                      onClick={() => setActivePrompt(key)}
                      style={{
                        padding: '10px 14px',
                        background: activePrompt === key ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${activePrompt === key ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.07)'}`,
                        borderLeft: `3px solid ${activePrompt === key ? '#c9a84c' : 'transparent'}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase',
                        color: activePrompt === key ? '#c9a84c' : 'rgba(212,197,169,0.5)',
                        textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}
                    >
                      <span>{icons[key]}</span>
                      {labels[key]}
                    </button>
                  );
                })}
              </div>

              {/* Prompt display + test */}
              <div>
                <div style={{
                  background: 'rgba(6,13,18,0.8)',
                  border: '1px solid rgba(201,168,76,0.15)',
                  borderRadius: 8,
                  padding: '20px',
                  marginBottom: 16,
                  maxHeight: 320,
                  overflowY: 'auto',
                }}>
                  <pre style={{
                    fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.7)',
                    lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {CLAUDE_SYSTEM_PROMPTS[activePrompt]}
                  </pre>
                </div>

                {/* Test prompt */}
                <div style={{
                  background: 'rgba(6,13,18,0.6)',
                  border: '1px solid rgba(45,138,135,0.2)',
                  borderRadius: 8,
                  padding: '16px 20px',
                }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(45,138,135,0.6)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
                    ◎ Test This Prompt with Claude
                  </div>
                  <textarea
                    value={claudeQuery}
                    onChange={e => setClaudeQuery(e.target.value)}
                    placeholder="Enter a test query to run against this system prompt..."
                    rows={3}
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: 6,
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                      color: '#F5F0E8', fontFamily: 'monospace', fontSize: 11,
                      outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                      marginBottom: 10,
                    }}
                  />
                  <button
                    onClick={runClaudeAnalysis}
                    disabled={isLoading || !claudeQuery.trim()}
                    style={{
                      fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                      padding: '8px 18px', borderRadius: 6, cursor: isLoading ? 'not-allowed' : 'pointer',
                      background: 'rgba(45,138,135,0.12)', border: '1px solid rgba(45,138,135,0.3)',
                      color: '#2d8a87',
                    }}
                  >
                    {isLoading ? '◌ Running...' : '◎ Run Claude Analysis'}
                  </button>

                  {response && (
                    <div style={{
                      marginTop: 16,
                      background: 'rgba(45,138,135,0.05)',
                      border: '1px solid rgba(45,138,135,0.15)',
                      borderRadius: 6,
                      padding: '14px 16px',
                      fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.7)',
                      lineHeight: 1.7, whiteSpace: 'pre-wrap',
                    }}>
                      {response}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── CRAWL LOG TAB ── */}
        {activeTab === 'crawl' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h2 style={{ fontFamily: "'Amiri', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 400, margin: '0 0 4px' }}>
                  Crawl Log
                </h2>
                <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', margin: 0 }}>
                  Live feed of crawler activity across all active regions and sources
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setCrawlLogs([])}
                  style={{
                    fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase',
                    padding: '8px 14px', borderRadius: 6, cursor: 'pointer',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                    color: 'rgba(212,197,169,0.4)',
                  }}
                >
                  Clear
                </button>
                <button
                  onClick={simulateCrawl}
                  disabled={isCrawling}
                  style={{
                    fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                    padding: '10px 20px', borderRadius: 6, cursor: isCrawling ? 'not-allowed' : 'pointer',
                    background: isCrawling ? 'rgba(45,122,71,0.05)' : 'rgba(45,122,71,0.12)',
                    border: `1px solid ${isCrawling ? 'rgba(45,122,71,0.15)' : 'rgba(45,122,71,0.35)'}`,
                    color: isCrawling ? 'rgba(45,122,71,0.4)' : '#2d7a47',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  {isCrawling ? (
                    <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>◌</span> Crawling…</>
                  ) : (
                    <>▶ Run Crawl</>
                  )}
                </button>
              </div>
            </div>

            <div style={{
              background: 'rgba(6,13,18,0.95)',
              border: '1px solid rgba(45,122,71,0.2)',
              borderRadius: 8,
              padding: '16px 20px',
              minHeight: 400,
              maxHeight: 600,
              overflowY: 'auto',
              fontFamily: 'monospace',
            }}>
              {crawlLogs.length === 0 ? (
                <div style={{ color: 'rgba(212,197,169,0.2)', fontSize: 11, textAlign: 'center', paddingTop: 60 }}>
                  No crawl logs yet. Click &ldquo;Run Crawl&rdquo; to start.
                </div>
              ) : (
                crawlLogs.map((log, i) => <CrawlLogLine key={i} log={log} />)
              )}
              <div ref={logsEndRef} />
            </div>
          </div>
        )}

        {/* ── RESULTS TAB ── */}
        {activeTab === 'results' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontFamily: "'Amiri', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 400, margin: '0 0 4px' }}>
                Crawl Results
              </h2>
              <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', margin: 0 }}>
                Articles extracted, categorized, and queued for newsletter review
              </p>
            </div>

            <div style={{
              background: 'rgba(6,13,18,0.6)',
              border: '1px solid rgba(201,168,76,0.15)',
              borderRadius: 10,
              padding: '40px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⬡</div>
              <div style={{ fontFamily: "'Georgia', serif", fontSize: 16, color: 'rgba(212,197,169,0.5)', marginBottom: 8 }}>
                Results will appear here after a crawl run
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', marginBottom: 20 }}>
                Go to the Crawl Log tab and click &ldquo;Run Crawl&rdquo; to populate results
              </div>
              <a
                href="/newsletter-validation"
                style={{
                  display: 'inline-block',
                  fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  color: '#c9a84c', background: 'rgba(201,168,76,0.08)',
                  border: '1px solid rgba(201,168,76,0.25)', borderRadius: 4, padding: '8px 16px',
                  textDecoration: 'none',
                }}
              >
                → View Newsletter Validation Dashboard
              </a>
            </div>
          </div>
        )}
      </div>

      <Footer />

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
